import { ApolloProvider } from '@apollo/client';
import * as React from 'react';
import { useEffect, type FC, type ReactElement } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';

import type { Runtime } from '@xing-com/crate-xinglet/internal';
import { RuntimeProvider } from '@xing-com/crate-xinglet/internal';

import { createApolloClient } from './create-apollo-client';

const LoadingStateHandler: FC<{ runtime: Runtime }> = ({ runtime }) => {
  useEffect(() => {
    function startWaiting(name: string): void {
      const eventual = runtime.cachedXinglets.get(name);
      if (!eventual || eventual.settled) return;
      if (waitingFor.add(name)) eventual.then(() => stopWaiting(name));
    }

    function stopWaiting(name: string): void {
      if (waitingFor.delete(name) && waitingFor.size === 0) {
        mountpoint?.setAttribute('data-xinglets-settled', `${counter++}`);
      }
    }

    function xingletAwaited(name: string): void {
      startWaiting(name);
    }

    let counter = 0;
    const mountpoint = document.querySelector('#app[data-mountpoint]');
    const waitingFor = new Set<string>();

    for (const name of runtime.cachedXinglets.keys()) {
      startWaiting(name);
    }

    if (waitingFor.size === 0) {
      mountpoint?.setAttribute('data-xinglets-settled', `${counter++}`);
    }

    runtime.events.on('xinglet-awaited', xingletAwaited);

    return () => {
      runtime.events.off('xinglet-awaited', xingletAwaited);
    };
  });

  return <></>;
};

export function createEntryPoint(
  runtime: Runtime,
  name: string,
  props: Record<string, unknown> = {}
): ReactElement {
  const { language } = runtime.config;
  const { languageData: messages = {} } = runtime;
  const apolloClient = createApolloClient(runtime);
  const helmetContext = runtime.isServer ? runtime.helmetContext : {};

  return (
    <ApolloProvider client={apolloClient}>
      <HelmetProvider context={helmetContext}>
        <IntlProvider
          locale={language}
          messages={messages}
          textComponent={React.Fragment}
        >
          <RuntimeProvider runtime={runtime}>
            <LoadingStateHandler runtime={runtime} />
            <runtime.host.XingletLoader
              {...props}
              runtime={runtime}
              name={name}
            />
          </RuntimeProvider>
        </IntlProvider>
      </HelmetProvider>
    </ApolloProvider>
  );
}
